import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ReportExportOptions {
  title: string;
  schoolName: string;
  dateRange: string;
  data: any[];
  columns: { header: string; key: string; width?: number }[];
  chartData?: {
    type: 'bar' | 'pie';
    data: any[];
    xKey?: string;
    yKey?: string;
  };
}

/**
 * Generate Excel file for reports with data tables
 */
export function generateReportExcel(options: ReportExportOptions): Blob {
  const { title, schoolName, dateRange, data, columns } = options;

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Create header rows
  const headerData = [
    [schoolName],
    [title],
    [`Date Range: ${dateRange}`],
    [], // Empty row
    columns.map(col => col.header),
  ];

  // Create data rows
  const dataRows = data.map(row =>
    columns.map(col => {
      const value = row[col.key];
      // Format numbers and dates appropriately
      if (typeof value === 'number') {
        return value;
      }
      return value?.toString() || '';
    })
  );

  // Combine header and data
  const wsData = [...headerData, ...dataRows];

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const colWidths = columns.map(col => ({
    wch: col.width || 15,
  }));
  ws['!cols'] = colWidths;

  // Style header rows
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let R = 0; R <= 2; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true, sz: R === 0 ? 16 : R === 1 ? 14 : 12 },
        alignment: { horizontal: 'center' },
      };
    }
  }

  // Style column headers (row 4, index 4)
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 4, c: C });
    if (!ws[cellAddress]) continue;
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'E0E0E0' } },
      alignment: { horizontal: 'center' },
    };
  }

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Report');

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Generate PDF file for reports with charts and tables
 */
export function generateReportPDF(options: ReportExportOptions): Blob {
  const { title, schoolName, dateRange, data, columns, chartData } = options;

  // Create PDF document
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Add school logo placeholder (if available)
  // TODO: Add actual logo when available
  // doc.addImage(logoData, 'PNG', 10, 10, 30, 30);

  // Add header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(schoolName, pageWidth / 2, 20, { align: 'center' });

  doc.setFontSize(14);
  doc.text(title, pageWidth / 2, 30, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Date Range: ${dateRange}`, pageWidth / 2, 38, { align: 'center' });

  // Add chart placeholder if chart data is provided
  let startY = 45;
  if (chartData) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('[Chart visualization - see Excel export for full details]', pageWidth / 2, startY, {
      align: 'center',
    });
    startY += 10;
  }

  // Add data table
  const tableColumns = columns.map(col => col.header);
  const tableRows = data.map(row => columns.map(col => row[col.key]?.toString() || ''));

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: startY,
    theme: 'grid',
    headStyles: {
      fillColor: [103, 80, 164], // Primary color
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 10, left: 10, right: 10 },
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
  });

  // Add footer with page numbers
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated on ${new Date().toLocaleDateString()}`,
      pageWidth - 10,
      pageHeight - 10,
      { align: 'right' }
    );
  }

  // Generate PDF blob
  return doc.output('blob');
}

/**
 * Generate descriptive filename for report export
 */
export function generateReportFilename(
  reportType: string,
  format: 'pdf' | 'xlsx',
  dateRange?: string
): string {
  const timestamp = new Date().toISOString().split('T')[0];
  const sanitizedType = reportType.replace(/\s+/g, '-').toLowerCase();
  const dateRangePart = dateRange ? `-${dateRange.replace(/\s+/g, '-')}` : '';
  return `${sanitizedType}-report${dateRangePart}-${timestamp}.${format}`;
}

/**
 * Download blob as file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
